
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Edit, Trash2, Plus, Package, ShoppingCart } from 'lucide-react';
import { ListeAvitaillement, ArticleAvitaillement } from '@/hooks/useListesAvitaillement';
import { toast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

interface DetailListeAvitaillementProps {
  liste: ListeAvitaillement;
  isOpen: boolean;
  onClose: () => void;
  onSave: (articles: ArticleAvitaillement[]) => void;
  onCreerInventaire: () => void;
  onCreerListeAchat: () => void;
}

const DetailListeAvitaillement = ({
  liste,
  isOpen,
  onClose,
  onSave,
  onCreerInventaire,
  onCreerListeAchat
}: DetailListeAvitaillementProps) => {
  const { t } = useTranslation('inventory');
  const [articles, setArticles] = useState<ArticleAvitaillement[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [newArticle, setNewArticle] = useState({
    nom: '',
    categorie: 'Alimentation',
    quantite: 1,
    unite: 'unité',
    priorite: 'normale' as 'normale' | 'critique'
  });

  useEffect(() => {
    if (liste) {
      setArticles([...liste.articles]);
    }
  }, [liste]);

  const handleSaveArticle = (index: number) => {
    if (editingIndex === index) {
      setEditingIndex(null);
      onSave([...articles]);
    }
  };

  const handleDeleteArticle = (index: number) => {
    const newArticles = articles.filter((_, i) => i !== index);
    setArticles(newArticles);
    onSave(newArticles);
  };

  const handleAddArticle = () => {
    if (!newArticle.nom.trim()) {
      toast({
        title: t('detailListe.error'),
        description: t('detailListe.articleNameRequired'),
        variant: "destructive",
      });
      return;
    }

    const articleToAdd: ArticleAvitaillement = {
      nom: newArticle.nom.trim(),
      categorie: newArticle.categorie,
      quantite: newArticle.quantite,
      unite: newArticle.unite,
      priorite: newArticle.priorite
    };

    const newArticles = [...articles, articleToAdd];
    setArticles(newArticles);
    onSave(newArticles);

    // Reset form
    setNewArticle({
      nom: '',
      categorie: 'Alimentation',
      quantite: 1,
      unite: 'unité',
      priorite: 'normale'
    });

    toast({
      title: t('detailListe.success'),
      description: t('detailListe.articleAdded'),
    });
  };

  const handleQuantiteChange = (index: number, value: number) => {
    const newArticles = [...articles];
    newArticles[index].quantite = Math.max(0, value);
    setArticles(newArticles);
  };

  const handleUniteChange = (index: number, value: string) => {
    const newArticles = [...articles];
    newArticles[index].unite = value;
    setArticles(newArticles);
  };

  const handleAjouterInventaire = () => {
    onCreerInventaire();
    onClose(); // Fermer la fenêtre de la liste
  };

  const categories = [
    'Alimentation', 'Boissons', 'Hygiène', 'Nettoyage', 'Sécurité',
    'Navigation', 'Confort', 'Technique', 'Vêtements', 'Divers'
  ];

  const unites = [
    'unité', 'kg', 'g', 'L', 'ml', 'cl', 'boîte', 'paquet', 'bouteille', 
    'tube', 'rouleau', 'sachet', 'pot', 'barquette', 'conserve'
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>{liste.nom}</span>
            <div className="flex gap-2">
              <Badge variant="outline">
                {liste.nombre_personnes} {t('detailListe.people')}
              </Badge>
              <Badge variant="outline">
                {liste.nombre_jours} {t('detailListe.days')}
              </Badge>
              <Badge variant="outline">
                {articles.length} {t('detailListe.articles')}
              </Badge>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Actions principales */}
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleAjouterInventaire}
              className="bg-nautic-gold text-nautic hover:bg-nautic-lightgold"
            >
              <Package className="h-4 w-4 mr-2" />
              {t('detailListe.addToInventory')}
            </Button>
            <Button 
              onClick={onCreerListeAchat}
              variant="outline"
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {t('detailListe.createPurchaseList')}
            </Button>
          </div>

          <Separator />

          {/* Ajouter un nouvel article */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Plus className="h-4 w-4" />
                {t('detailListe.addArticleTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="lg:col-span-2">
                  <Label htmlFor="nom">{t('detailListe.articleName')}</Label>
                  <Input
                    id="nom"
                    value={newArticle.nom}
                    onChange={(e) => setNewArticle(prev => ({ ...prev, nom: e.target.value }))}
                    placeholder={t('detailListe.articleNamePlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="categorie">{t('detailListe.category')}</Label>
                  <Select
                    value={newArticle.categorie}
                    onValueChange={(value) => setNewArticle(prev => ({ ...prev, categorie: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="quantite">{t('detailListe.quantity')}</Label>
                  <Input
                    id="quantite"
                    type="number"
                    min="1"
                    value={newArticle.quantite}
                    onChange={(e) => setNewArticle(prev => ({ ...prev, quantite: parseInt(e.target.value) || 1 }))}
                  />
                </div>
                <div>
                  <Label htmlFor="unite">{t('detailListe.unit')}</Label>
                  <Select
                    value={newArticle.unite}
                    onValueChange={(value) => setNewArticle(prev => ({ ...prev, unite: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {unites.map(unite => (
                        <SelectItem key={unite} value={unite}>{unite}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <Button onClick={handleAddArticle}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('detailListe.add')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Liste des articles */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('detailListe.listArticlesTitle', { count: articles.length })}</CardTitle>
            </CardHeader>
            <CardContent>
              {articles.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  {t('detailListe.noArticles')}
                </p>
              ) : (
                <div className="space-y-2">
                  {articles.map((article, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
                        <div>
                          <p className="font-medium">{article.nom}</p>
                          <p className="text-sm text-muted-foreground">{article.categorie}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {editingIndex === index ? (
                            <>
                              <Input
                                type="number"
                                min="0"
                                value={article.quantite}
                                onChange={(e) => handleQuantiteChange(index, parseInt(e.target.value) || 0)}
                                className="w-20"
                              />
                              <Select
                                value={article.unite}
                                onValueChange={(value) => handleUniteChange(index, value)}
                              >
                                <SelectTrigger className="w-24">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {unites.map(unite => (
                                    <SelectItem key={unite} value={unite}>{unite}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </>
                          ) : (
                            <span className="font-medium">
                              {article.quantite} {article.unite}
                            </span>
                          )}
                        </div>
                        <div className="text-center">
                          <Badge variant={article.priorite === 'critique' ? 'destructive' : 'secondary'}>
                            {article.priorite}
                          </Badge>
                        </div>
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              if (editingIndex === index) {
                                handleSaveArticle(index);
                              } else {
                                setEditingIndex(index);
                              }
                            }}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteArticle(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions finales */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              {t('detailListe.close')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DetailListeAvitaillement;
